import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import {
  Badge,
  Button,
  ChannelAvatar,
  ChevronRightIcon,
  SquarePenIcon,
  EmptyState,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'

import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { PostTypeIcon } from '~publish/components/PostType'
import { useChannelsConnected } from '~publish/contexts/AccountContext'
import { readFragment } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { UseComposerResult } from '~publish/hooks/usePostComposer'
import { useTimeFormatPreference } from '~publish/hooks/useTimeFormatPreference'
import { useTimezone } from '~publish/hooks/useTimezone'
import { schedule } from '~publish/legacy/routes'
import { PusherEvent } from '~publish/services/pusher'

import { formatUpNextTime } from '../UpNextSection/formatUpNextTime'
import { useRealtimeRefetch } from '../useRealtimeRefetch'

import { useEditContentItemDraft } from './useEditContentItemDraft'
import { useLatestDrafts } from './useLatestDrafts'
import type { DraftPost, LatestDraft } from './useLatestDrafts'

import styles from './DraftsSection.module.css'
import { SectionHeader } from '../SectionHeader'

// Every event that can change draft membership: create/edit/delete, the
// draft ↔ queue moves (approve emits DRAFT_APPROVED, move-to-drafts emits
// POST_UPDATED or DRAFT_MOVED), and Publish Now on a draft, which signals
// only sent_update on success and failed_update on failure.
const DRAFTS_REALTIME_EVENTS = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_DELETED,
  PusherEvent.POST_SENT,
  PusherEvent.POST_FAILED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_APPROVED,
]

// Draft events are user-driven and low volume, so a short window keeps the
// list snappy — mirrors Up Next rather than the 30s comments default.
const DRAFTS_REFETCH_THROTTLE_MS = 3_000

const DRAFTS_EDIT_POST_CTA = 'publish-home-draftsCard-editPost-1'

const EDIT_LABEL_MAX_LENGTH = 60

const draftTextSnippet = (text: string): string | null => {
  const trimmed = text.trim()
  if (!trimmed) {
    return null
  }
  return trimmed.length > EDIT_LABEL_MAX_LENGTH
    ? `${trimmed.slice(0, EDIT_LABEL_MAX_LENGTH)}…`
    : trimmed
}

const draftCardBaseName = (draft: LatestDraft): string => {
  if (draft.kind === 'post') {
    const snippet = draftTextSnippet(draft.post.text ?? '')
    if (snippet) {
      return `Edit draft "${snippet}"`
    }
    return `Edit Draft for ${draft.post.channel.name}`
  }

  const body = isOfType(draft.contentItem.body, 'DraftContent')
    ? draft.contentItem.body
    : null
  const snippet = draftTextSnippet(body?.text ?? '')
  if (snippet) {
    return `Edit draft "${snippet}"`
  }
  const title = draft.contentItem.title?.trim()
  if (title) {
    return `Edit ${title}`
  }
  return 'Edit Draft'
}

const draftCardExtraName = (
  draft: LatestDraft,
  timezone: string,
  timeFormat: '12' | '24',
): string => {
  const time =
    draft.kind === 'post' && draft.post.dueAt
      ? formatUpNextTime(draft.post.dueAt, timezone, timeFormat)
      : `saved ${formatUpNextTime(draft.createdAt, timezone, timeFormat)}`

  if (draft.kind === 'post' && draftTextSnippet(draft.post.text ?? '')) {
    return `${draft.post.channel.name}, ${time}`
  }
  return time
}

const disambiguateNames = (
  bases: readonly string[],
  extras: readonly string[],
): string[] => {
  const baseCounts = new Map<string, number>()
  for (const base of bases) {
    baseCounts.set(base, (baseCounts.get(base) ?? 0) + 1)
  }

  const withExtras = bases.map((base, index) => {
    if ((baseCounts.get(base) ?? 0) < 2) {
      return base
    }
    const extra = extras[index]
    return extra ? `${base}, ${extra}` : base
  })

  const extraCounts = new Map<string, number>()
  for (const name of withExtras) {
    extraCounts.set(name, (extraCounts.get(name) ?? 0) + 1)
  }

  const reservedNames = new Set(
    withExtras.filter((name) => (extraCounts.get(name) ?? 0) === 1),
  )
  const usedNames = new Set<string>()
  const seen = new Map<string, number>()
  return withExtras.map((name) => {
    if ((extraCounts.get(name) ?? 0) < 2) {
      usedNames.add(name)
      return name
    }

    let next = (seen.get(name) ?? 0) + 1
    while (
      reservedNames.has(`${name} ${next}`) ||
      usedNames.has(`${name} ${next}`)
    ) {
      next += 1
    }
    seen.set(name, next)
    const uniqueName = `${name} ${next}`
    usedNames.add(uniqueName)
    return uniqueName
  })
}

const uniqueDraftCardNames = (
  drafts: LatestDraft[],
  timezone: string,
  timeFormat: '12' | '24',
): string[] => {
  const bases = drafts.map((draft) => draftCardBaseName(draft))
  const extras = drafts.map((draft) =>
    draftCardExtraName(draft, timezone, timeFormat),
  )
  return disambiguateNames(bases, extras)
}

/**
 * The latest drafts across channel drafts and channel-less drafts, rendered
 * as a single row of cards spanning the full page width. Cards share the row
 * equally; a lone draft is paired with an informational empty slot so it
 * doesn't stretch across the whole page. Renders nothing at all when the
 * organization has no drafts (or while that is still unknown), so there is
 * no section-level loading/empty/error state.
 */
export function DraftsSection(): JSX.Element | null {
  const { drafts, totalCount, refetch } = useLatestDrafts()
  const timezone = useTimezone()
  const timeFormat = useTimeFormatPreference()
  const labeledDrafts = useMemo(() => {
    const names = uniqueDraftCardNames(drafts, timezone, timeFormat)
    return drafts.map((draft, index) => ({
      draft,
      accessibleName: names[index] ?? draftCardBaseName(draft),
    }))
  }, [drafts, timezone, timeFormat])

  const { editPostInComposer, triggerAttributes } = usePostComposer()

  const channels = useChannelsConnected()
  const allowedChannelIds = useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  useRealtimeRefetch({
    events: DRAFTS_REALTIME_EVENTS,
    refetch,
    allowedChannelIds,
    throttleMs: DRAFTS_REFETCH_THROTTLE_MS,
  })

  if (drafts.length === 0) {
    return null
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <SectionHeader>Latest Drafts</SectionHeader>
          <Text as="span" size="sm" color="subtle">
            <span className={styles.leadingSeparator}>· </span>
            {totalCount} saved
          </Text>
        </div>
        <Button
          as={RouterLink}
          to={schedule.getRoute({ tab: 'drafts' })}
          size="small"
          variant="tertiary"
          className={styles.seeAll}
          aria-label="View all drafts"
        >
          View All
          <ChevronRightIcon />
        </Button>
      </div>

      <ul className={styles.list}>
        {labeledDrafts.map(({ draft, accessibleName }) =>
          draft.kind === 'post' ? (
            <DraftPostCard
              key={draft.post.id}
              post={draft.post}
              accessibleName={accessibleName}
              triggerAttributes={triggerAttributes}
              editPostInComposer={editPostInComposer}
            />
          ) : (
            <DraftContentItemCard
              key={draft.contentItem.id}
              contentItem={draft.contentItem}
              accessibleName={accessibleName}
              triggerAttributes={triggerAttributes}
            />
          ),
        )}
        {drafts.length === 1 && (
          <li className={styles.emptySlot}>
            <EmptyState size="small" className={styles.emptyState}>
              <EmptyState.Heading>
                No more drafts in progress
              </EmptyState.Heading>
              <EmptyState.Description>
                Your latest drafts will show up here
              </EmptyState.Description>
            </EmptyState>
          </li>
        )}
      </ul>
    </div>
  )
}

type DraftPostCardProps = {
  post: DraftPost
  accessibleName: string
  triggerAttributes: UseComposerResult['triggerAttributes']
  editPostInComposer: UseComposerResult['editPostInComposer']
}

function DraftPostCard({
  post,
  accessibleName,
  triggerAttributes,
  editPostInComposer,
}: DraftPostCardProps): JSX.Element {
  const timezone = useTimezone()
  const timeFormat = useTimeFormatPreference()

  const dueAtLabel = post.dueAt
    ? formatUpNextTime(post.dueAt, timezone, timeFormat)
    : ''
  const isOverdue = post.dueAt ? Date.parse(post.dueAt) < Date.now() : false

  const firstAsset = post.assets?.[0]
  const thumbnail = firstAsset?.thumbnail ?? firstAsset?.source ?? null
  const channelName = post.channel.name

  return (
    <li className={styles.card}>
      <button
        type="button"
        className={styles.cardButton}
        aria-label={accessibleName}
        {...triggerAttributes}
        onClick={() =>
          void editPostInComposer({
            postId: post.id,
            cta: DRAFTS_EDIT_POST_CTA,
          })
        }
      >
        <span className={styles.cardHeader}>
          <ChannelAvatar
            size="small"
            alt={`${channelName} avatar`}
            src={post.channel.avatar ?? undefined}
            channel={post.channel.service}
            className={styles.avatar}
          />
          {dueAtLabel && (
            <Text size="sm" color="subtle" className={styles.time}>
              {dueAtLabel}
            </Text>
          )}
          <span className={styles.cardHeaderMeta}>
            {isOverdue && (
              <Badge variant="warning" size="small">
                <WarningIcon />
                Overdue
              </Badge>
            )}
            <span
              className={styles.postTypeIcon}
              data-testid="drafts-post-type"
            >
              <PostTypeIcon type={post.metadata?.type} />
            </span>
          </span>
        </span>
        <DraftCardBody text={post.text} thumbnail={thumbnail}>
          Draft for <span translate="no">{channelName}</span>
        </DraftCardBody>
      </button>
    </li>
  )
}

type DraftContentItemCardProps = {
  contentItem: ContentItemDraft
  accessibleName: string
  triggerAttributes: UseComposerResult['triggerAttributes']
}

function DraftContentItemCard({
  contentItem,
  accessibleName,
  triggerAttributes,
}: DraftContentItemCardProps): JSX.Element | null {
  const edit = useEditContentItemDraft(contentItem)

  // The drafts query only returns DraftContent bodies; guard so the card is
  // safe to render for any ContentItem without leaking a channel-post body.
  const body = isOfType(contentItem.body, 'DraftContent')
    ? contentItem.body
    : null
  if (!body) {
    return null
  }

  const assets = readFragment(PostMediaAsset_Asset, body.assets)
  const firstAsset = assets?.[0]
  const thumbnail = firstAsset?.thumbnail ?? firstAsset?.source ?? null

  return (
    <li className={styles.card}>
      <button
        type="button"
        className={styles.cardButton}
        aria-label={accessibleName}
        {...triggerAttributes}
        onClick={edit}
      >
        <span className={styles.cardHeader}>
          <span className={styles.contentItemIcon} data-testid="drafts-pen">
            <SquarePenIcon size="small" />
          </span>
        </span>
        <DraftCardBody
          text={body.text || contentItem.title || 'Draft'}
          thumbnail={thumbnail}
        />
      </button>
    </li>
  )
}

type DraftCardBodyProps = {
  text: string | null
  thumbnail: string | null
  /** Caption fallback for when the draft has no text. */
  children?: ReactNode
}

function DraftCardBody({
  text,
  thumbnail,
  children,
}: DraftCardBodyProps): JSX.Element {
  return (
    <span className={styles.cardBody}>
      {text ? (
        <Text className={styles.caption} translate="no">
          {text}
        </Text>
      ) : (
        <Text className={styles.caption}>{children}</Text>
      )}
      {thumbnail && (
        <span className={styles.thumbnail}>
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            data-testid="drafts-thumbnail"
          />
        </span>
      )}
    </span>
  )
}
